import {useState, useRef, useCallback, useEffect} from 'react'
import type {RefObject} from 'react'
import {IconButton, type IconButtonProps} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type {Icon} from '@primer/octicons-react'
import type {Repository} from '@github-ui/current-repository'
import {useSearchParams} from '@github-ui/use-navigate'
import {NewAgentTaskForm, type NewAgentTaskFormProps} from '@github-ui/new-agent-task-form/NewAgentTaskForm'
import {useComposerModeControls} from '@github-ui/agent-sessions/hooks/use-composer-mode-controls'
import {useCreateAPITask} from '@github-ui/agent-sessions/hooks/use-create-api-task'
import {useEnvironmentTaskSubmit} from '@github-ui/agent-sessions/hooks/use-environment-task-submit'
import {TaskCreationFailedBannerList} from '@github-ui/agent-sessions/components/TaskCreationFailedBanner'
import type {CreateAPITaskParams} from '@github-ui/agent-sessions/types/api-task'
import {repoTasksQueryKey, REPO_TASKS_PAGE_SIZE} from '@github-ui/agent-sessions/hooks/repo-task-utils'
import {resolveRepositoryNwo} from './resolve-repository-nwo'

export interface NewRepoAgentTaskButtonProps extends Pick<IconButtonProps, 'size' | 'variant' | 'tooltipDirection'> {
  repository: Repository
  currentUserId?: number
  currentUserLogin?: string
  icon: Icon
  label?: string
  eventType: string
  initialText?: string
  /** Maximum height for the textarea input in pixels */
  maxTextareaHeight?: number
  /** If provided, the component becomes controlled */
  isOpen?: boolean
  /** Callback when dialog should close (required if isOpen is provided) */
  onOpenChange?: (open: boolean) => void
  /** Optional identifiers for tracking (e.g., parent workflow_run_id) */
  eventIdentifiers?: string[]
  /** Whether task mode is enabled. When false, createPullRequest defaults to true. */
  taskModeEnabled?: boolean
  /** Query key for the active task list. When provided, optimistic updates target this cache entry. */
  taskListQueryKey?: readonly unknown[]
  /** Opt in to the feature-flagged AHP environment picker. Only the repo-agents
   * sidebar composer enables it; other consumers keep the cloud-task-only flow. */
  enableEnvironmentPicker?: boolean
  /** Surfaces the managed-sandbox picker option (requires enableEnvironmentPicker). */
  supportsManagedSandbox?: boolean
  /** Shows the agent-mode and tool-permission picker, matching the full-page composer. */
  enableModeControls?: boolean
  /** Hide the built-in button when an external control owns the controlled open state. */
  hideTrigger?: boolean
  /** Element that receives focus when an externally triggered dialog closes. */
  returnFocusRef?: RefObject<HTMLElement | null>
}

export function NewRepoAgentTaskButton({
  repository,
  currentUserId,
  currentUserLogin,
  icon,
  label = 'New session',
  size,
  variant,
  tooltipDirection,
  eventType,
  initialText,
  maxTextareaHeight,
  isOpen: controlledIsOpen,
  onOpenChange,
  eventIdentifiers,
  taskModeEnabled,
  taskListQueryKey,
  enableEnvironmentPicker,
  supportsManagedSandbox,
  enableModeControls,
  hideTrigger = false,
  returnFocusRef,
}: NewRepoAgentTaskButtonProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Support both controlled and uncontrolled modes
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen

  const handleOpen = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(true)
    } else {
      setInternalIsOpen(true)
    }
  }, [isControlled, onOpenChange])

  const handleClose = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(false)
    } else {
      setInternalIsOpen(false)
    }
  }, [isControlled, onOpenChange])

  const dialogTitle = `${label} in ${repository.ownerLogin}/${repository.name}`

  return (
    <>
      {!hideTrigger && (
        <IconButton
          ref={buttonRef}
          icon={icon}
          aria-label={label}
          size={size}
          tooltipDirection={tooltipDirection}
          variant={variant}
          onClick={handleOpen}
        />
      )}
      {isOpen && (
        <Dialog
          title={dialogTitle}
          onClose={handleClose}
          returnFocusRef={returnFocusRef ?? buttonRef}
          position={{narrow: 'fullscreen'}}
        >
          <Dialog.Body className="p-0">
            <NewRepoAgentTaskDialogContent
              repository={repository}
              currentUserId={currentUserId}
              currentUserLogin={currentUserLogin}
              eventType={eventType}
              onClose={handleClose}
              initialText={initialText}
              maxTextareaHeight={maxTextareaHeight}
              eventIdentifiers={eventIdentifiers}
              taskModeEnabled={taskModeEnabled}
              taskListQueryKey={taskListQueryKey}
              enableEnvironmentPicker={enableEnvironmentPicker}
              supportsManagedSandbox={supportsManagedSandbox}
              enableModeControls={enableModeControls}
            />
          </Dialog.Body>
        </Dialog>
      )}
    </>
  )
}

interface NewRepoAgentTaskDialogContentProps {
  repository: Repository
  currentUserId?: number
  currentUserLogin?: string
  eventType: string
  onClose: () => void
  initialText?: string
  maxTextareaHeight?: number
  eventIdentifiers?: string[]
  taskModeEnabled?: boolean
  taskListQueryKey?: readonly unknown[]
  enableEnvironmentPicker?: boolean
  supportsManagedSandbox?: boolean
  enableModeControls?: boolean
}

function NewRepoAgentTaskDialogContent({
  repository,
  currentUserId,
  currentUserLogin,
  eventType,
  onClose,
  initialText,
  maxTextareaHeight,
  eventIdentifiers,
  taskModeEnabled,
  taskListQueryKey,
  enableEnvironmentPicker,
  supportsManagedSandbox,
  enableModeControls,
}: NewRepoAgentTaskDialogContentProps) {
  const modeControls = useComposerModeControls()
  const [searchParams] = useSearchParams()
  const authorFilter = searchParams.get('author')
  const isFilteredByCurrentUser = currentUserLogin ? authorFilter === currentUserLogin : false
  const queryKey =
    taskListQueryKey ??
    repoTasksQueryKey(
      repository.ownerLogin,
      repository.name,
      isFilteredByCurrentUser && currentUserId ? [currentUserId] : undefined,
      1 /* page */,
      false,
    )

  const taskListMutationConfig = {
    queryKey,
    pageSize: REPO_TASKS_PAGE_SIZE,
  }
  const {createTask} = useCreateAPITask(taskListMutationConfig)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const {onEnvironmentSubmit, failedEnvironmentAttempts, handleEnvironmentDismiss} = useEnvironmentTaskSubmit({
    optimisticTaskListConfig: taskListMutationConfig,
    textAreaRef,
    repositoryCloneUrl: repository.httpCloneUrl,
  })

  useEffect(() => {
    // Autofocus the textarea when the dialog opens
    textAreaRef.current?.focus()
  }, [])

  const handleCreateTask = useCallback(
    (params: CreateAPITaskParams) => {
      createTask({
        ...params,
        eventType,
        eventIdentifiers,
      })
    },
    [createTask, eventType, eventIdentifiers],
  )

  const handleEnvironmentSubmit = useCallback(
    async (input: Parameters<NonNullable<NewAgentTaskFormProps['onEnvironmentSubmit']>>[0]) => {
      await onEnvironmentSubmit({
        ...input,
        repositoryNwo: resolveRepositoryNwo(input.repositoryNwo, repository),
      })
      onClose()
    },
    [onEnvironmentSubmit, onClose, repository],
  )

  return (
    <>
      <NewAgentTaskForm
        createTask={handleCreateTask}
        currentRepo={repository}
        onCreate={onClose}
        hideRepoSelector
        eventType={eventType}
        showHeader={false}
        textAreaRef={textAreaRef}
        initialText={initialText}
        maxTextareaHeight={maxTextareaHeight}
        taskModeEnabled={taskModeEnabled}
        onEnvironmentSubmit={enableEnvironmentPicker ? handleEnvironmentSubmit : undefined}
        directTurnTelemetrySurface="new_repository_task"
        supportsManagedSandbox={supportsManagedSandbox}
        {...(enableModeControls ? modeControls : {})}
      />
      {enableEnvironmentPicker && (
        <TaskCreationFailedBannerList
          attempts={failedEnvironmentAttempts}
          onDismiss={handleEnvironmentDismiss}
          className="px-3 pb-3"
        />
      )}
    </>
  )
}
